import markerSDK from '@marker.io/browser';
import { Flag06 as FlagIcon } from '@untitledui/icons';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { getOptionalEnv } from '../lib/getEnv';

import { useAuthState } from './AuthProvider';

const MARKER_PROJECT_ID = getOptionalEnv('VITE_MARKER_PROJECT_ID');

interface IMarkerContext {
  capture: () => void;
}

const MarkerContext = React.createContext<IMarkerContext>({ capture: () => {} });

export const useMarker = () => React.useContext(MarkerContext);

type IMarkerProviderProps = React.PropsWithChildren;

/**
 * Container element for the custom report button, rendered as a portal into
 * document.body with data-react-aria-top-layer so it stays interactive when
 * React Aria modals/slideouts are open.
 */
function useMarkerPortal() {
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = document.createElement('div');
    el.dataset.reactAriaTopLayer = 'true';
    document.body.appendChild(el);
    setContainer(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  return container;
}

const MarkerProvider = ({ children }: IMarkerProviderProps) => {
  const { user } = useAuthState();
  const portalContainer = useMarkerPortal();
  const [isWidgetReady, setIsWidgetReady] = React.useState(!!window.Marker);

  const capture = React.useCallback(() => {
    if (window.Marker) {
      void window.Marker.capture('fullscreen');
    }
  }, []);

  // Initialize the widget once (only if project ID is configured).
  // Uses window.Marker as the source of truth rather than a React ref,
  // because React 18 Strict Mode double-mounts break the ref pattern.
  React.useEffect(() => {
    if (window.Marker || !MARKER_PROJECT_ID) {
      return;
    }
    let cancelled = false;

    const hideDefaultButton = () => {
      const feedbackButton = document.getElementById('feedback-button');
      if (feedbackButton) {
        feedbackButton.style.setProperty('display', 'none', 'important');
      }
    };

    void markerSDK.loadWidget({ project: MARKER_PROJECT_ID }).then(() => {
      if (cancelled) {
        return;
      }

      // Hide the default Marker.io button via CSS — the SDK's hide()
      // method puts the widget in a state that ignores capture() calls.
      hideDefaultButton();

      // Re-hide whenever Marker.io tries to show its default button
      // (e.g. after a capture flow ends).
      window.Marker.on('show', hideDefaultButton);

      // Tag the Marker.io container so React Aria's ariaHideOutside
      // keeps it interactive when modals/slideouts are open (avoids inert).
      const markerContainer = document.querySelector<HTMLElement>('div.marker-app');
      if (markerContainer) {
        markerContainer.dataset.reactAriaTopLayer = 'true';
      }

      setIsWidgetReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Update reporter info when user changes or widget becomes ready.
  // The isWidgetReady dependency ensures reporter is set even if the user
  // was already logged in when loadWidget resolved.
  React.useEffect(() => {
    if (!isWidgetReady) {
      return;
    }
    if (user) {
      window.Marker.setReporter({
        email: user.email ?? undefined,
        fullName: user.name ?? undefined,
      });
    } else {
      window.Marker.clearReporter();
    }
  }, [user, isWidgetReady]);

  return (
    <MarkerContext.Provider value={{ capture }}>
      {children}
      {MARKER_PROJECT_ID && portalContainer && isWidgetReady
        ? createPortal(<ReportButton onClick={capture} />, portalContainer)
        : null}
    </MarkerContext.Provider>
  );
};

interface IReportButtonProps {
  onClick: () => void;
}

const ReportButton = ({ onClick }: IReportButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className="bg-error-solid ring-error_subtle hover:bg-error-solid_hover hover:ring-error shadow-xs z-9999 fixed bottom-3 right-5 flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-white ring-1 transition-colors"
  >
    <FlagIcon className="size-3.5 shrink-0" />
    Report
  </button>
);

export default MarkerProvider;
